<template>
  <div class="items main-content">
    <div class="page-header">
      <div class="d-flex flex-row">
        <div>
          <h3 class="page-title">{{ $tc("backups.backup", 2) }}</h3>
        </div>
      </div>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="dashboard">
            {{ $t("general.home") }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="#">
            {{ $tc("backups.backup", 2) }}
          </router-link>
        </li>
      </ol>
      <div class="page-actions row">
        <div slot="item-title" class="col-xs-2">
          <base-button
            color="theme"
            icon="plus"
            size="large"
            @click="newBackup()"
          >
            {{ $t("backups.add_backup") }}
          </base-button>
        </div>
      </div>
    </div>

    <div
      v-cloak
      v-show="showEmptyScreen"
      class="col-xs-1 no-data-info"
      align="center"
    >
      <satellite-icon class="mt-5 mb-4" />
      <div class="row" align="center">
        <label class="col title">{{ $t("backups.no_backups") }}</label>
      </div>
      <div class="row">
        <label class="description col mt-1" align="center">{{
          $t("backups.list_of_backups")
        }}</label>
      </div>
      <div class="btn-container">
        <base-button
          :outline="true"
          color="theme"
          class="mt-3"
          size="large"
          @click="newBackup()"
        >
          {{ $t("backups.add_new_backup") }}
        </base-button>
      </div>
    </div>

    <div v-show="!showEmptyScreen" class="table-container">
      <div class="table-actions mt-5">
        <p class="table-stats">
          {{ $t("general.showing") }}: <b>{{ backups.length }}</b>
          {{ $t("general.of") }} <b>{{ totalBackups }}</b>
        </p>
        <transition name="fade">
          <v-dropdown v-if="selectedBackups.length" :show-arrow="false">
            <span
              slot="activator"
              href="#"
              class="table-actions-button dropdown-toggle"
            >
              {{ $t("general.actions") }}
            </span>
            <v-dropdown-item>
              <div class="dropdown-item" @click="removeMultipleBackups">
                <font-awesome-icon
                  :icon="['fas', 'trash']"
                  class="dropdown-item-icon"
                />
                {{ $t("general.delete") }}
              </div>
            </v-dropdown-item>
          </v-dropdown>
        </transition>
      </div>

      <div class="custom-control custom-checkbox">
        <input
          id="select-all"
          v-model="selectAllFieldStatus"
          type="checkbox"
          class="custom-control-input"
          @change="selectAllBackups"
        />
        <label
          v-show="!isRequestOngoing"
          for="select-all"
          class="custom-control-label selectall"
        >
          <span class="select-all-label">{{ $t("general.select_all") }} </span>
        </label>
      </div>

      <table-component
        ref="table"
        :data="fetchData"
        :show-filter="false"
        table-class="table"
      >
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="no-click"
        >
          <template slot-scope="row">
            <div class="custom-control custom-checkbox">
              <input
                :id="row.file_name"
                v-model="selectField"
                :value="row.file_name"
                type="checkbox"
                class="custom-control-input"
              />
              <label :for="row.file_name" class="custom-control-label" />
            </div>
          </template>
        </table-column>
        <table-column :label="$t('backups.file_name')" show="file_name" />
        <table-column :label="$t('backups.file_size')" show="file_size" />
        <table-column :label="$t('backups.added_on')" show="last_modified" />
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="action-dropdown"
        >
          <template slot-scope="row">
            <span> {{ $t("backups.actions") }} </span>
            <v-dropdown>
              <a slot="activator" href="#">
                <dot-icon />
              </a>
              <v-dropdown-item>
                <div
                  class="dropdown-item"
                  @click="downloadedBackup(row.file_name)"
                >
                  <font-awesome-icon
                    icon="download"
                    class="dropdown-item-icon"
                  />
                  {{ $t("backups.download") }}
                </div>
              </v-dropdown-item>
              <v-dropdown-item>
                <div class="dropdown-item" @click="removeBackup(row.file_name)">
                  <font-awesome-icon
                    :icon="['fas', 'trash']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.delete") }}
                </div>
              </v-dropdown-item>
            </v-dropdown>
          </template>
        </table-column>
      </table-component>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DotIcon from "../../components/icon/DotIcon";
import SatelliteIcon from "../../components/icon/SatelliteIcon";
import BaseButton from "../../../js/components/base/BaseButton";

export default {
  components: {
    DotIcon,
    SatelliteIcon,
    BaseButton
  },
  data() {
    return {
      id: null,
      showFilters: false,
      isRequestOngoing: true
    };
  },
  computed: {
    ...mapGetters("Backup", [
      "backups",
      "totalBackups",
      "selectedBackups",
      "selectAllField"
    ]),
    ...mapGetters("currency", ["defaultCurrency"]),
    showEmptyScreen() {
      return !this.totalBackups && !this.isRequestOngoing;
    },
    selectField: {
      get: function() {
        return this.selectedBackups;
      },
      set: function(val) {
        this.selectBackup(val);
      }
    },
    selectAllFieldStatus: {
      get: function() {
        return this.selectAllField;
      },
      set: function(val) {
        this.setSelectAllState(val);
      }
    }
  },
  watch: {},

  destroyed() {
    if (this.selectAllField) {
      this.selectAllBackups();
    }
  },
  methods: {
    ...mapActions("Backup", [
      "fetchBackups",
      "deleteBackup",
      "downloadBackup",
      "addBackup",
      "selectBackup",
      "deleteMultipleBackups",
      "selectAllBackups",
      "setSelectAllState"
    ]),
    refreshTable() {
      this.$refs.table.refresh();
    },
    async fetchData({ page, per_page }) {
      let data = { page, per_page };
      this.isRequestOngoing = true;
      let response = await this.fetchBackups(data);
      this.isRequestOngoing = false;

      return {
        data: response.data.backups.data,
        pagination: {
          totalPages: response.data.backups.last_page,
          currentPage: page,
          rowsPerPage: response.data.backups.per_page // Add rowsPerPage in the API request
        }
      };
    },

    async newBackup() {
      let response = await this.addBackup();
      if (response.data) {
        this.isLoading = false;
        window.toastr["success"](this.$tc("backups.created_message"));
        this.$refs.table.refresh();
        return true;
      }
      window.toastr["error"](response.data.error);
      this.$refs.table.refresh();
    },
    async removeBackup(file_name) {
      this.file_name = file_name;
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("backups.confirm_delete"),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let data = {
            file_name: file_name
          };
          let res = await this.deleteBackup(data);
          if (res.data.success) {
            window.toastr["success"](this.$tc("backups.deleted_message", 1));
            this.$refs.table.refresh();
            return true;
          }
          window.toastr["error"](res.data.message);
          return true;
        }
      });
    },
    async downloadedBackup(file_name) {
      let data = {
        file_name: file_name
      };
      let response = await this.downloadBackup(data);
      if (response.data.error) {
        window.toastr["error"](response.data.error);
        return true;
      }
    },
    async removeMultipleBackups() {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("backups.confirm_delete", 2),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let res = await this.deleteMultipleBackups();
          if (res.data.success || res.data.backups) {
            window.toastr["success"](this.$tc("backups.deleted_message", 2));
            this.$refs.table.refresh();
          } else if (res.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    }
  }
};
</script>
